import { Navigate, useParams } from "react-router";
import { ExternalLink, Eye, Loader2 } from "lucide-react";
import { Button } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useAbrirNaOrganizacao } from "@/lib/use-abrir-na-organizacao";
import { OrganizacaoEmFoco } from "@/components/organization/painel/OrganizacaoEmFoco";
import { PatientDetail } from "./PatientDetail";

/**
 * Ficha de um paciente de outra clínica, aberta pelo acesso pessoal sem trocar a organização ativa.
 * Mostra o que o papel naquela clínica deixa ver; daqui só agenda e pede prótese — para alterar,
 * abre na clínica (troca, com Desfazer).
 */
export function FichaEmOutraClinica() {
  const { organizacaoId, id } = useParams<{ organizacaoId: string; id: string }>();
  const { user } = useAuth();
  const { abrir } = useAbrirNaOrganizacao();
  const minhas = trpc.organization.listMine.useQuery();
  const org = minhas.data?.find((m) => m.organization.id === organizacaoId)?.organization;

  if (user?.activeOrganization?.id === organizacaoId) return <Navigate to={`/pacientes/${id}`} replace />;
  if (minhas.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  if (!org || !id) return <p className="py-16 text-center text-sm text-zinc-500">Você não faz parte dessa organização.</p>;

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-2.5 dark:border-border dark:bg-muted/30">
        <Eye className="h-4 w-4 shrink-0 text-zinc-500" />
        <p className="min-w-0 flex-1 text-[13px] text-zinc-700 dark:text-foreground">
          Ficha de <strong>{org.name}</strong>, só leitura. Daqui dá para agendar consulta e pedir prótese nessa clínica.
        </p>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => abrir(org.id, org.name, `/pacientes/${id}`)}>
          <ExternalLink className="h-3.5 w-3.5" />
          Abrir na clínica para editar
        </Button>
      </div>
      <OrganizacaoEmFoco key={org.id} organizationId={org.id} somenteLeitura>
        <PatientDetail />
      </OrganizacaoEmFoco>
    </div>
  );
}
